import { Component, computed, ElementRef, inject, OnDestroy, signal, viewChild } from '@angular/core';
import { BaseDialogComponent } from '@cumlaude/shared-components-dialogs';
import { IconComponent } from '@cumlaude/shared-components-icons';
import { Dialog, DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { AttrPath } from '@cumlaude/metadata';
import { attrLabel } from '../../../services/labels';
import { QueryParamStateService } from '../../../services/query-param-state.service';
import { GroeperingSorterenDialogComponent } from '../groepering-sorteren-dialog/groepering-sorteren-dialog.component';
import { ConnectedPosition, Overlay } from '@angular/cdk/overlay';
import { firstValueFrom } from 'rxjs';
import { SortDirection } from '../../../shared/components/table/table/table.component';
import { CategoryFields } from '../../../services/metadata';
import { Categorie, Groepering, GroeperingListComponent } from '../groepering-list/groepering-list.component';
import { AlleGroeperingenDialogComponent, AlleGroeperingenOptions } from '../alle-groeperingen-dialog/alle-groeperingen-dialog.component';
import { GroeperingService } from '../../../services/groepering.service';

export interface GroeperingOptions {
	huidigeGroepering?: AttrPath;
	voorgesteldeGroeperingen: AttrPath[];
	alleGroeperingen: CategoryFields[];
	huidigeGroeperingen: AttrPath[];
	deletable: boolean;
	lockable: boolean;
}

@Component({
	selector: 'app-groepering-dialog',
	templateUrl: './groepering-dialog.component.html',
	styleUrls: ['./groepering-dialog.component.scss'],
	imports: [BaseDialogComponent, GroeperingListComponent, IconComponent],
})
export class GroeperingDialogComponent implements OnDestroy {
	protected readonly options = inject<GroeperingOptions>(DIALOG_DATA);
	protected readonly dialogRef = inject<DialogRef>(DialogRef);
	protected readonly qp = inject(QueryParamStateService);
	protected readonly groeperingService = inject(GroeperingService);
	private readonly dialog = inject(Dialog);
	private readonly overlay = inject(Overlay);

	readonly groeperingen = computed<Categorie[]>(() => {
		const { huidigeGroepering, voorgesteldeGroeperingen, huidigeGroeperingen } = this.options;

		const groeperingen = voorgesteldeGroeperingen
			.filter((groepering) => {
				const groupString = groepering.join('.');
				if (huidigeGroepering?.join('.') === groupString) return true;

				return !huidigeGroeperingen.some((userGroep) => userGroep.join('.') === groupString);
			})
			.map((groepering) => {
				return <Groepering>{
					attrPath: groepering,
					naam: attrLabel(groepering),
					current: huidigeGroepering?.join('.') === groepering.join('.'),
				};
			})
			.sort((groupA, groupB) => groupA.naam.localeCompare(groupB.naam));

		return [{ groeperingen }];
	});

	readonly hasAlleGroeperingen = computed<boolean>(() => this.options.alleGroeperingen.length > 0);

	readonly sorteerElement = viewChild<ElementRef>('sorteren');
	readonly alleGroeperingenElement = viewChild<ElementRef>('alleGroeperingen');
	readonly containerElement = viewChild<ElementRef>('container');

	readonly menuSorterenLaatste = signal(false);
	readonly menuAlleGroeperingenLaatste = signal(false);

	lockedColumn = this.qp.signal('lockedColumn');

	readonly isLocked = computed(() => {
		const lockedColumn = this.lockedColumn();
		if (!lockedColumn) return false;

		const huidigeGroepering = this.options.huidigeGroepering;
		if (!huidigeGroepering) return false;

		return lockedColumn === huidigeGroepering.join('.');
	});

	protected sorteerDialogRef?: DialogRef<SortDirection, GroeperingSorterenDialogComponent>;
	protected alleGroeperingenDialogRef?: DialogRef<Groepering, AlleGroeperingenDialogComponent>;

	ngOnDestroy() {
		this.closeSubMenus();
	}

	select(groepering: Groepering) {
		if (!groepering.current) this.updateGroeperingen(groepering.attrPath);

		this.dialogRef.close();
	}

	async openAlleGroeperingenDialog() {
		const alleGroeperingenElement = this.alleGroeperingenElement();
		if (!alleGroeperingenElement) return;

		const containerElement = this.containerElement();
		if (!containerElement) return;

		const menuAlleGroeperingenLaatste = this.menuAlleGroeperingenLaatste();
		if (menuAlleGroeperingenLaatste) return;

		this.closeSubMenus();
		this.menuAlleGroeperingenLaatste.set(true);

		const viewportMargin = 16;
		const minSubmenuHeight = 240;
		const submenuWidth = 288; // size-18 (18rem) — zie AlleGroeperingenDialogComponent's sizeClass

		const panelRect = containerElement.nativeElement.getBoundingClientRect();
		const boundingClientRect = alleGroeperingenElement.nativeElement.getBoundingClientRect();

		// Ankeren op het hoofdmenu zelf (niet de trigger-rij), zodat het submenu de volle band tussen
		// de bovenkant van het hoofdmenu en de onderkant van de pagina gebruikt.
		const topBoundary = Math.max(panelRect.top, viewportMargin);
		const bottomBoundary = window.innerHeight - viewportMargin;
		const height = bottomBoundary - topBoundary;

		// Spiegelen als de kolom tegen de rechterrand van het scherm staat en het submenu daar niet past.
		const spaceRight = window.innerWidth - boundingClientRect.right - viewportMargin;
		const mirrored = spaceRight < submenuWidth;

		// Eén zelf-berekende positie i.p.v. CDK meerdere kandidaten laten afwegen: met
		// withFlexibleDimensions(true) wist CDK de max-height van de pane (verplaatst die naar een
		// niet-clippende wrapper), waardoor de lijst onscrollbaar buiten het scherm valt.
		const position: ConnectedPosition = {
			originX: mirrored ? 'start' : 'end',
			originY: 'top',
			overlayX: mirrored ? 'end' : 'start',
			overlayY: 'top',
			offsetX: mirrored ? -1 : 1,
			offsetY: 0,
		};

		this.alleGroeperingenDialogRef = this.dialog.open<Groepering, AlleGroeperingenOptions, AlleGroeperingenDialogComponent>(
			AlleGroeperingenDialogComponent,
			{
				data: {
					huidigeGroepering: this.options.huidigeGroepering,
					alleGroeperingen: this.options.alleGroeperingen,
					huidigeGroeperingen: this.options.huidigeGroeperingen,
				},
				maxHeight: `${height}px`,
				minHeight: `${Math.min(minSubmenuHeight, height)}px`,
				hasBackdrop: false,
				positionStrategy: this.overlay
					.position()
					.flexibleConnectedTo(containerElement)
					.withFlexibleDimensions(false)
					.withPush(false)
					.withViewportMargin(viewportMargin)
					.withPositions([position]),
			}
		);

		const groepering = await firstValueFrom(this.alleGroeperingenDialogRef.closed);
		if (!groepering) return;

		this.select(groepering);
	}

	async openSortDialog() {
		const sorteerElement = this.sorteerElement();
		if (!sorteerElement) return;

		const huidigeGroepering = this.options.huidigeGroepering;
		if (!huidigeGroepering) return;

		const menuSorterenLaatste = this.menuSorterenLaatste();
		if (menuSorterenLaatste) return;

		this.closeSubMenus();
		this.menuSorterenLaatste.set(true);

		this.sorteerDialogRef = this.dialog.open<SortDirection, void, GroeperingSorterenDialogComponent>(GroeperingSorterenDialogComponent, {
			hasBackdrop: false,
			positionStrategy: this.overlay
				.position()
				.flexibleConnectedTo(sorteerElement)
				.withFlexibleDimensions(false)
				.withPositions([
					{
						originX: 'end',
						originY: 'top',
						overlayX: 'start',
						overlayY: 'top',
						offsetX: 1,
						offsetY: 0,
					},
				]),
		});

		const sortOrder = await firstValueFrom(this.sorteerDialogRef.closed);
		if (!sortOrder) return;

		const newSort = { direction: sortOrder, active: huidigeGroepering.join('.') };
		this.qp.dispatch('sortOrder', newSort);
	}

	delete() {
		this.updateGroeperingen();
		this.dialogRef.close();
	}

	changeLockedColumn() {
		const huidigeGroepering = this.options.huidigeGroepering;
		if (!huidigeGroepering) return;

		const currentLockedColumn = this.lockedColumn();
		if (currentLockedColumn === huidigeGroepering.join('.')) {
			this.qp.dispatch('lockedColumn', undefined);
		} else {
			this.qp.dispatch('lockedColumn', huidigeGroepering.join('.'));
		}

		this.dialogRef.close();
	}

	private updateGroeperingen(groepering?: AttrPath) {
		const huidigeGroeperingen = this.options.huidigeGroeperingen;
		let changedGroeperingen = [...huidigeGroeperingen];
		const huidigeGroepering = this.options.huidigeGroepering;
		if (huidigeGroepering) {
			const ix = huidigeGroeperingen.findIndex((group) => group.join('.') === huidigeGroepering.join('.'));
			if (groepering) {
				changedGroeperingen[ix] = groepering;
			} else {
				changedGroeperingen.splice(ix, 1);
			}
		} else if (groepering) {
			changedGroeperingen = [...changedGroeperingen, groepering];
		}

		this.qp.dispatch_g(changedGroeperingen);
	}

	closeSubMenus() {
		this.menuSorterenLaatste.set(false);
		this.menuAlleGroeperingenLaatste.set(false);
		this.sorteerDialogRef?.close();
		this.alleGroeperingenDialogRef?.close();
	}
}
